import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, task } from 'ember-concurrency';
import { createURN } from 'garaje/models/generic-resource';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type { ResourceAmenityType } from 'garaje/types/generic-resources/ResourceTypeConfig';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { defer } from 'rsvp';

interface ResourceSettingsAmenitiesComponentArgs {
  resourceTypeId: number;
  onLoad?: () => Promise<ResourceAmenityType[]>;
  onDelete?: (id: number) => Promise<void>;
  onCreate?: (name: string) => Promise<number>;
}

export default class ResourceSettingsAmenitiesComponent extends Component<ResourceSettingsAmenitiesComponentArgs> {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare ajax: AjaxFetchService;

  @tracked amenities: ResourceAmenityType[] = [];
  @tracked newAmenity: string = '';
  @tracked amenityErrorLabel: string = '';

  constructor(owner: unknown, args: ResourceSettingsAmenitiesComponentArgs) {
    super(owner, args);

    void this.getAvailableAmenities.perform();
  }

  deleteAmenity = async (id: number): Promise<void> => {
    if (this.args.onDelete) {
      return await this.args.onDelete(id);
    }

    const url = urlBuilder.rms.amenity(this.args.resourceTypeId, String(id));

    await this.ajax.request(url, {
      type: 'DELETE',
      contentType: 'application/json',
    });
  };

  createAmenity = async (): Promise<number> => {
    if (this.args.onCreate) {
      return await this.args.onCreate(this.newAmenity);
    }

    const url = urlBuilder.rms.amenities(this.args.resourceTypeId, this.state.currentLocation.id);

    const { data }: { data: { resourceTypeAmenityId: number } } = await this.ajax.request(url, {
      type: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        data: {
          name: this.newAmenity,
          resourceTypeId: this.args.resourceTypeId,
          relationId: createURN('location', this.state.currentLocation.id),
        },
      }),
    });

    return data.resourceTypeAmenityId;
  };

  getAvailableAmenities = task({ restartable: true }, async () => {
    if (this.args.onLoad) {
      this.amenities = await this.args.onLoad();
      return;
    }

    const url = urlBuilder.rms.amenities(this.args.resourceTypeId, this.state.currentLocation.id);

    const response: { data: ResourceAmenityType[] } = await this.ajax.request(url, {
      type: 'GET',
      contentType: 'application/json',
    });

    this.amenities = response.data;
  });

  @dropTask
  showDeleteModalTask = {
    *perform(
      this: {
        context: ResourceSettingsAmenitiesComponent;
        abort?: () => void;
        continue?: () => Promise<void>;
      },
      amenity: ResourceAmenityType,
    ): Generator<Promise<boolean>, void, void> {
      const deferred = defer<boolean>();

      this.abort = () => {
        deferred.resolve(false);
      };

      this.continue = async () => {
        try {
          await this.context.deleteAmenity(amenity.resourceTypeAmenityId);

          this.context.amenities = this.context.amenities.filter(
            (a) => a.resourceTypeAmenityId !== amenity.resourceTypeAmenityId,
          );
          this.context.flashMessages.showAndHideFlash('success', 'Deleted!');

          deferred.resolve(true);
        } catch (e) {
          this.context.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        }
      };

      return yield deferred.promise;
    },
  };

  @dropTask
  showCreateModalTask = {
    *perform(this: {
      context: ResourceSettingsAmenitiesComponent;
      abort?: () => void;
      continue?: () => Promise<void>;
    }): Generator<Promise<boolean>, void, void> {
      const deferred = defer<boolean>();

      this.abort = () => {
        this.context.newAmenity = '';
        this.context.amenityErrorLabel = '';

        deferred.resolve(false);
      };

      this.continue = async () => {
        try {
          if (!this.context.newAmenity.trim()) {
            this.context.amenityErrorLabel = 'You must provide an amenity name';
            return;
          }

          const id = await this.context.createAmenity();

          this.context.amenities = [
            ...this.context.amenities,
            {
              resourceTypeAmenityId: id,
              name: this.context.newAmenity,
              relationId: createURN('location', this.context.state.currentLocation.id),
              resourceTypeId: this.context.args.resourceTypeId,
            },
          ];

          this.context.flashMessages.showAndHideFlash('success', 'Saved!');

          this.context.newAmenity = '';
          this.context.amenityErrorLabel = '';

          deferred.resolve(true);
        } catch (e) {
          this.context.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        }
      };

      return yield deferred.promise;
    },
  };
}
